/**
 * PointPicker: the Points step for a non-Modbus device (web brief U5). The
 * driver's browse kind decides the view; every view adds {@link PickedPoint}s
 * ({metric, address, data_type}) that become the Map step's rows.
 *
 * - node-tree (OPC UA): a lazily expanded tree; expanding a branch browses its
 *   `cursor`, a leaf's `address` is its node id.
 * - tag-list (EtherNet/IP): a paged table with checkboxes. A controller with no
 *   tag list answers `browse_unsupported`, which falls back to manual entry.
 * - block-scan (S7, MELSEC): a blind, read-only walk of the PLC's memory, one
 *   page per browse call (~3 s or 16 blocks), following `next`, each page shown
 *   as it arrives with where it is and a Stop button (design 7.4.1).
 * - none / anything unknown: manual entry only.
 *
 * Below the view, the point map: the same editable table and toolbar as the
 * Modbus register map (PointMapParts). Each picked point's type, name, unit and
 * scale are edited in place (and its word order, where the driver's address
 * has one: MELSEC). "Add row" opens the driver's address fields (from its
 * address_schema, checked as the platform checks that driver's address,
 * lib/iot/address-validate), open from the start when the device lists
 * nothing; "Import CSV/JSON" previews a point map row by row before it is
 * applied, and "Export" saves the map for the next device of the same kind.
 * A footer sticks above the step footer with the count, so the picks stay in
 * sight down a long scan. The block scan's "also list zeros" sits under its
 * options, and "32-bit" on a pair of adjacent words opens the interpret-as
 * choices (float32 / int32 / uint32 with the decoded value;
 * lib/iot/word-decode), adding the point with that type in one click.
 */

import { Fragment, useId, useMemo, useRef, useState } from "react";
import {
  Binary,
  Check,
  ChevronDown,
  ChevronRight,
  CircleStop,
  ListChecks,
  ListTree,
  Loader2,
  Play,
  Plus,
  ScanLine,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { Disclosure } from "@/lib/ui/disclosure";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";
import { Table, type ColumnDef } from "@/lib/widgets";
import { isLiveApi, type BrowseEntry, type DriverInfo, type PointDataType } from "@/lib/api";
import { errorCodeOf } from "@/lib/api/error";
import { gateway, scanErrorMessage } from "@/lib/gateway-client";
import { browseKindOf, driverLabel } from "@/lib/iot/drivers";
import {
  POINT_DATA_TYPES,
  addressKey,
  appendBlockPage,
  blockProgress,
  browseEntryToPoint,
  firstBlockCursor,
  pointAddressLabel,
  valueText,
  type EntryGroup,
  type PickedPoint,
} from "@/lib/iot/points";
import { formDefaults, formToParams, validateParams, type FormValues } from "@/lib/iot/schema-form";
import { SchemaForm } from "@/components/shared/SchemaForm";
import { interpretations, pairToPoint, wordPairs, type WordPair } from "@/lib/iot/word-decode";
import { addressFieldsOf, validateAddress } from "@/lib/iot/address-validate";
import {
  METRIC_PATTERN,
  editPoint,
  mergePoints,
  pointIdentity,
  pointNameProblems,
  pointSampled,
  pointToRecord,
  pointWordOrder,
  wordOrderApplies,
  type PointEdit,
} from "@/lib/iot/point-map-io";
import {
  ImportPreview,
  KnownMapPicker,
  MapFileButtons,
  MapFooter,
  MapFormatHelp,
  ScaleInput,
  WordOrderPicker,
  type MapPreview,
} from "./PointMapParts";
import { DeviceErrorText } from "@/components/connect/DeviceErrorText";

export interface PointPickerProps {
  gatewayId: string | null;
  gatewayName: string;
  /** The chosen driver's registry entry (schemas, browse kind, data types); undefined without a drivers list. */
  driver: DriverInfo | undefined;
  driverId: string;
  /** The device's link params from the Protocol step (secrets inline, used for the read only). */
  link: Record<string, unknown>;
  deviceLabel: string;
  /** The device's product name as the scan reported it (identity.product); points out a known map built for it. */
  deviceProduct?: string;
  picked: PickedPoint[];
  onPicked: (next: PickedPoint[]) => void;
  /** "Bring in selected": go on to the Map step with the picked points. */
  onContinue?: () => void;
}

/** Most block-scan rows kept on screen (a walk can list thousands of words). */
const MAX_BLOCK_ENTRIES = 2000;

export function PointPicker(props: PointPickerProps) {
  const { driver, driverId, picked, onPicked } = props;
  const kind = browseKindOf(driverId, driver ? [driver] : undefined);
  const [manualOnly, setManualOnly] = useState<string | null>(null);
  const decodable = driver?.data_types;

  const pickedKeys = useMemo(() => new Set(picked.map((p) => p.key)), [picked]);
  const pickedByKey = useMemo(() => new Map(picked.map((p) => [p.key, p])), [picked]);
  // A 32-bit reading of two words replaces either word picked on its own.
  const pickInterpreted = (p: PickedPoint, coverKey: string) =>
    onPicked([...picked.filter((x) => x.key !== p.key && x.key !== coverKey), p]);
  const toggle = (p: PickedPoint, on: boolean) => {
    if (on && !pickedKeys.has(p.key)) onPicked([...picked, p]);
    if (!on) onPicked(picked.filter((x) => x.key !== p.key));
  };
  const pickMany = (ps: PickedPoint[], on: boolean) => {
    if (on) onPicked([...picked, ...ps.filter((p) => !pickedKeys.has(p.key))]);
    else {
      const drop = new Set(ps.map((p) => p.key));
      onPicked(picked.filter((x) => !drop.has(x.key)));
    }
  };
  const common = {
    ...props,
    pickedKeys,
    pickedByKey,
    pickInterpreted,
    toggle,
    pickMany,
    decodable,
    onUnsupported: setManualOnly,
  };
  const listsNothing = !!manualOnly || !["node-tree", "tag-list", "block-scan"].includes(kind);

  return (
    <div className="flex flex-col gap-4">
      {manualOnly ? (
        <Notice tone="info">{manualOnly}</Notice>
      ) : kind === "node-tree" ? (
        <NodeTreePicker {...common} />
      ) : kind === "tag-list" ? (
        <TagListPicker {...common} />
      ) : kind === "block-scan" ? (
        <BlockScanPicker {...common} />
      ) : (
        <Notice tone="info">
          {driverLabel(driverId, driver ? [driver] : undefined)} devices do not list their points, so enter each
          point&apos;s address below.
        </Notice>
      )}
      <PointMap {...props} listsNothing={listsNothing} onAdd={(p) => toggle(p, true)} existing={pickedKeys} />
    </div>
  );
}

interface ViewProps extends PointPickerProps {
  pickedKeys: Set<string>;
  pickedByKey: Map<string, PickedPoint>;
  /** Pick a 32-bit reading of two words, dropping either word picked on its own. */
  pickInterpreted: (p: PickedPoint, coverKey: string) => void;
  toggle: (p: PickedPoint, on: boolean) => void;
  pickMany: (ps: PickedPoint[], on: boolean) => void;
  decodable?: readonly PointDataType[];
  /** The device has no listing: show this and keep manual entry. */
  onUnsupported: (message: string) => void;
}

/** A browse failure as an operator-facing message; `browse_unsupported` goes to manual entry. */
function browseErrorFor(p: ViewProps) {
  return (e: unknown): string | null => {
    const msg = scanErrorMessage(e, { op: "browse", gatewayName: p.gatewayName, deviceIp: p.deviceLabel });
    if (errorCodeOf(e) === "browse_unsupported") {
      p.onUnsupported(`${p.deviceLabel} does not list its points. Enter each point's address below.`);
      return null;
    }
    return msg;
  };
}

// ── node-tree (OPC UA) ──────────────────────────────────────────────────────

interface Level {
  entries: BrowseEntry[];
  next: Record<string, unknown> | null;
  loading: boolean;
  error?: string;
}

function NodeTreePicker(p: ViewProps) {
  const [levels, setLevels] = useState<Record<string, Level>>({});
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toError = browseErrorFor(p);
  const canBrowse = isLiveApi && !!p.gatewayId;

  const load = async (key: string, cursor: Record<string, unknown> | undefined, more: boolean) => {
    if (!p.gatewayId) return;
    setLevels((l) => ({
      ...l,
      [key]: { ...(l[key] ?? { entries: [], next: null }), loading: true, error: undefined },
    }));
    try {
      const res = await gateway.browse(p.gatewayId, { driver: p.driverId, link: p.link, cursor });
      setLevels((l) => ({
        ...l,
        [key]: {
          entries: more ? [...(l[key]?.entries ?? []), ...res.entries] : res.entries,
          next: res.next ?? null,
          loading: false,
        },
      }));
    } catch (e) {
      const error = toError(e) ?? undefined;
      setLevels((l) => ({ ...l, [key]: { ...(l[key] ?? { entries: [], next: null }), loading: false, error } }));
    }
  };

  const expand = (key: string, entry: BrowseEntry) => {
    const next = new Set(open);
    if (next.has(key)) next.delete(key);
    else {
      next.add(key);
      if (!levels[key]) void load(key, entry.cursor, false);
    }
    setOpen(next);
  };

  const root = levels[""];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ListTree className="size-5 text-brand" />
          Browse {p.deviceLabel}
        </CardTitle>
        <CardDescription>
          Open the folders and tick the values to read (read live through {p.gatewayName}).
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {!root ? (
          <Button className="self-start" onClick={() => void load("", undefined, false)} disabled={!canBrowse}>
            <ListTree /> Browse the server
          </Button>
        ) : (
          <TreeLevel
            levelKey=""
            level={root}
            depth={0}
            levels={levels}
            open={open}
            onExpand={expand}
            onMore={(key, cursor) => void load(key, cursor, true)}
            view={p}
          />
        )}
        {!canBrowse && <p className="text-xs text-on-surface-muted">Browsing needs the live API and a gateway.</p>}
      </CardContent>
    </Card>
  );
}

function TreeLevel({
  levelKey,
  level,
  depth,
  levels,
  open,
  onExpand,
  onMore,
  view,
}: {
  levelKey: string;
  level: Level;
  depth: number;
  levels: Record<string, Level>;
  open: Set<string>;
  onExpand: (key: string, entry: BrowseEntry) => void;
  onMore: (key: string, cursor: Record<string, unknown>) => void;
  view: ViewProps;
}) {
  return (
    <ul
      role={depth === 0 ? "tree" : "group"}
      aria-label={depth === 0 ? "Server address space" : undefined}
      className={cn("flex flex-col", depth > 0 && "ml-5 border-l border-border pl-2")}
    >
      {level.entries.map((entry, i) => {
        const key = `${levelKey}/${entry.cursor ? addressKey(entry.cursor) : `${entry.name}#${i}`}`;
        const point = browseEntryToPoint(view.driverId, entry, view.decodable);
        const expanded = open.has(key);
        return (
          <li
            key={key}
            role="treeitem"
            aria-expanded={entry.has_children ? expanded : undefined}
            aria-selected={point ? view.pickedKeys.has(point.key) : undefined}
            className="flex flex-col"
          >
            <div className="flex items-center gap-1.5 py-0.5 text-sm">
              {entry.has_children ? (
                <button
                  type="button"
                  aria-label={`${expanded ? "Collapse" : "Expand"} ${entry.name}`}
                  onClick={() => onExpand(key, entry)}
                  className="rounded p-0.5 text-on-surface-muted hover:text-on-surface"
                >
                  {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                </button>
              ) : (
                <span className="w-4.5" aria-hidden />
              )}
              {point ? (
                <label className="flex min-w-0 flex-1 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={view.pickedKeys.has(point.key)}
                    onChange={(e) => view.toggle(point, e.target.checked)}
                  />
                  <span className="truncate text-on-surface">{entry.name}</span>
                  <Badge variant="secondary" className="font-mono text-[10px]">
                    {point.data_type}
                  </Badge>
                  {entry.value !== undefined && (
                    <span className="font-mono text-[11px] text-on-surface-muted">{valueText(entry.value)}</span>
                  )}
                </label>
              ) : (
                <span className="text-on-surface">{entry.name}</span>
              )}
            </div>
            {expanded && levels[key] && (
              <TreeLevel
                levelKey={key}
                level={levels[key]}
                depth={depth + 1}
                levels={levels}
                open={open}
                onExpand={onExpand}
                onMore={onMore}
                view={view}
              />
            )}
          </li>
        );
      })}
      {level.loading && (
        <li className="flex items-center gap-2 py-1 text-xs text-on-surface-muted">
          <Loader2 className="size-3.5 animate-spin" /> Reading…
        </li>
      )}
      {level.error && (
        <li>
          <Notice tone="error">
            <DeviceErrorText message={level.error} />
          </Notice>
        </li>
      )}
      {!level.loading && level.entries.length === 0 && !level.error && (
        <li className="py-1 text-xs text-on-surface-muted">Nothing here.</li>
      )}
      {level.next && !level.loading && (
        <li>
          <Button size="sm" variant="ghost" onClick={() => onMore(levelKey, level.next!)}>
            More…
          </Button>
        </li>
      )}
    </ul>
  );
}

// ── tag-list (EtherNet/IP) ─────────────────────────────────────────────────

function TagListPicker(p: ViewProps) {
  const [entries, setEntries] = useState<BrowseEntry[] | null>(null);
  const [next, setNext] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toError = browseErrorFor(p);
  const canBrowse = isLiveApi && !!p.gatewayId && !loading;

  const load = async (more: boolean) => {
    if (!p.gatewayId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await gateway.browse(p.gatewayId, {
        driver: p.driverId,
        link: p.link,
        cursor: more && next ? next : undefined,
      });
      setEntries((prev) => (more ? [...(prev ?? []), ...res.entries] : res.entries));
      setNext(res.next ?? null);
    } catch (e) {
      setError(toError(e));
    } finally {
      setLoading(false);
    }
  };

  const points = (entries ?? [])
    .map((e) => browseEntryToPoint(p.driverId, e, p.decodable))
    .filter((x): x is PickedPoint => x !== null);
  const allOn = points.length > 0 && points.every((x) => p.pickedKeys.has(x.key));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ListTree className="size-5 text-brand" />
          Controller tags — {p.deviceLabel}
        </CardTitle>
        <CardDescription>
          List the controller&apos;s tags through {p.gatewayName}, then tick the ones to read.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void load(false)} disabled={!canBrowse}>
            {loading ? <Loader2 className="animate-spin" /> : <ListTree />}
            {entries ? "List again" : "List tags"}
          </Button>
          {next && entries && (
            <Button variant="outline" onClick={() => void load(true)} disabled={!canBrowse}>
              More tags
            </Button>
          )}
        </div>
        {error && (
          <Notice tone="error">
            <DeviceErrorText message={error} />
          </Notice>
        )}
        {entries && <PointTable points={points} view={p} allOn={allOn} empty="The controller listed no tags." />}
      </CardContent>
    </Card>
  );
}

function PointTable({
  points,
  view,
  allOn,
  empty,
  pairs,
}: {
  points: PickedPoint[];
  view: ViewProps;
  allOn: boolean;
  empty: string;
  /** Adjacent word pairs (block scan), keyed by the first word: offers "32-bit" on that row. */
  pairs?: Map<string, WordPair>;
}) {
  const [interpretOpen, setInterpretOpen] = useState<Set<string>>(new Set());
  // Second words covered by a picked 32-bit reading of their pair.
  const coveredBy = new Map<string, PickedPoint>();
  for (const pair of pairs?.values() ?? []) {
    const pk = view.pickedByKey.get(pair.firstKey);
    if (pk && pk.data_type !== "int16" && pk.data_type !== "uint16") coveredBy.set(pair.secondKey, pk);
  }
  if (points.length === 0) return <p className="text-xs text-on-surface-muted">{empty}</p>;
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted/40 text-left text-[11px] uppercase tracking-wide text-on-surface-muted">
          <tr>
            <th className="w-8 px-2 py-1.5">
              <input
                type="checkbox"
                aria-label="Select all"
                checked={allOn}
                onChange={(e) => view.pickMany(points, e.target.checked)}
              />
            </th>
            <th className="px-2 py-1.5">Point</th>
            <th className="px-2 py-1.5">Type</th>
            <th className="px-2 py-1.5 text-right">Value</th>
          </tr>
        </thead>
        <tbody>
          {points.map((pt) => {
            const pair = pairs?.get(pt.key);
            const pickedAs = view.pickedByKey.get(pt.key);
            const retyped = pickedAs && pickedAs.data_type !== pt.data_type ? pickedAs : null;
            const covered = coveredBy.get(pt.key);
            const open = !!pair && interpretOpen.has(pt.key);
            return (
              <Fragment key={pt.key}>
                <tr className="border-t border-border">
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      aria-label={`Read ${pt.label}`}
                      checked={view.pickedKeys.has(pt.key) || !!covered}
                      disabled={!!covered}
                      onChange={(e) => view.toggle(pt, e.target.checked)}
                    />
                  </td>
                  <td className="px-2 py-1 font-mono text-xs text-on-surface">
                    {pt.label}
                    {covered && (
                      <span className="ml-2 font-sans text-[11px] text-on-surface-muted">
                        read with {covered.label}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    <span className="flex items-center gap-1.5">
                      <Badge variant={retyped ? "default" : "secondary"} className="font-mono text-[10px]">
                        {retyped ? retyped.data_type : pt.data_type}
                      </Badge>
                      {pair && (
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-label={`Read ${pt.label} and the next word as one 32-bit value`}
                          title="Read this word and the next as one 32-bit value (float32, int32, uint32)"
                          onClick={() => {
                            const next = new Set(interpretOpen);
                            if (next.has(pt.key)) next.delete(pt.key);
                            else next.add(pt.key);
                            setInterpretOpen(next);
                          }}
                          className={cn(
                            "flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            open
                              ? "bg-brand-muted text-brand"
                              : "text-on-surface-muted hover:bg-surface-muted hover:text-on-surface",
                          )}
                        >
                          <Binary className="size-3" />
                          32-bit
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right font-mono text-xs text-on-surface-muted">
                    {valueText(retyped ? retyped.value : pt.value)}
                  </td>
                </tr>
                {open && pair && (
                  <tr className="bg-surface-muted/30">
                    <td />
                    <td colSpan={3} className="px-2 py-2">
                      <InterpretRow pair={pair} view={view} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The interpret-as choices for two words: each 32-bit type with its decoded value, added in one click. */
function InterpretRow({ pair, view }: { pair: WordPair; view: ViewProps }) {
  const current = view.pickedByKey.get(pair.firstKey);
  const reads = interpretations(pair, view.decodable);
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] text-on-surface-muted">
        {pair.label} = 0x{pair.u32.toString(16).toUpperCase().padStart(8, "0")}, read as:
      </span>
      <div className="flex flex-wrap gap-1.5">
        {reads.map((r) => {
          const added = current?.data_type === r.type;
          return (
            <button
              key={r.type}
              type="button"
              disabled={added}
              onClick={() => view.pickInterpreted(pairToPoint(pair, r.type), pair.secondKey)}
              className={cn(
                "flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                added
                  ? "border-success/50 bg-success/10 text-success"
                  : "border-border bg-surface hover:border-brand/60",
              )}
            >
              <span className="font-mono text-on-surface-muted">{r.type}</span>
              <span className="font-mono font-semibold text-on-surface">{r.text}</span>
              {added ? (
                <span className="flex items-center gap-0.5">
                  <Check className="size-3" /> Added
                </span>
              ) : (
                <span className="flex items-center gap-0.5 text-brand">
                  <Plus className="size-3" /> Add
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── block-scan (S7, MELSEC) ─────────────────────────────────────────────────

function BlockScanPicker(p: ViewProps) {
  const [groups, setGroups] = useState<EntryGroup[]>([]);
  const [cursor, setCursor] = useState<Record<string, unknown> | null>(null);
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);
  const [capped, setCapped] = useState(false);
  const [showZeros, setShowZeros] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const stopRef = useRef(false);
  const seenRef = useRef(new Set<string>());
  const toError = browseErrorFor(p);
  const zerosId = useId();

  const count = groups.reduce((n, g) => n + g.entries.length, 0);

  const run = async (from: Record<string, unknown>, have: EntryGroup[]) => {
    if (!p.gatewayId) return;
    stopRef.current = false;
    setRunning(true);
    setError(null);
    let at: Record<string, unknown> | null = from;
    let list = have;
    try {
      while (at && !stopRef.current) {
        const sent: Record<string, unknown> = at;
        setCursor(sent);
        const res = await gateway.browse(p.gatewayId, { driver: p.driverId, link: p.link, cursor: sent });
        const total = list.reduce((n, g) => n + g.entries.length, 0);
        const add = appendBlockPage(
          list,
          res.entries,
          p.driverId,
          seenRef.current,
          Math.max(0, MAX_BLOCK_ENTRIES - total),
        );
        list = add.groups;
        setGroups(list);
        const next = res.next ?? null;
        const stuck = next !== null && JSON.stringify(next) === JSON.stringify(sent);
        if (add.truncated || (next && total + add.added >= MAX_BLOCK_ENTRIES)) {
          setCapped(true);
          at = null;
          setCursor(next);
          break;
        }
        if (!next || stuck) {
          setDone(true);
          at = null;
          setCursor(null);
          break;
        }
        at = next;
        setCursor(next);
      }
    } catch (e) {
      setError(toError(e));
    } finally {
      setRunning(false);
    }
  };

  const start = () => {
    seenRef.current = new Set();
    setGroups([]);
    setDone(false);
    setCapped(false);
    setStarted(true);
    void run(firstBlockCursor(showZeros), []);
  };

  const toggleGroup = (key: string) => {
    const next = new Set(openGroups);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOpenGroups(next);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ScanLine className="size-5 text-brand" />
          Scan {p.deviceLabel}&apos;s memory
        </CardTitle>
        <CardDescription>
          The gateway reads the PLC&apos;s memory in small blocks, read-only; tick the words to read.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {!running && (
            <Button onClick={start} disabled={!isLiveApi || !p.gatewayId}>
              <Play /> {started ? "Scan again" : "Start scan"}
            </Button>
          )}
          {running && (
            <Button
              variant="outline"
              onClick={() => {
                stopRef.current = true;
              }}
            >
              <CircleStop /> Stop
            </Button>
          )}
          {!running && started && !done && cursor && (
            <Button variant="outline" onClick={() => void run(cursor, groups)}>
              <Play /> Resume
            </Button>
          )}
        </div>
        <Disclosure
          summary="Scan options"
          hint={showZeros ? "every word, zeros included" : "only words that hold data"}
          keepMounted
        >
          <label htmlFor={zerosId} className="flex items-center gap-1.5 text-xs text-on-surface-muted">
            <input
              id={zerosId}
              type="checkbox"
              checked={showZeros}
              disabled={running}
              onChange={(e) => setShowZeros(e.target.checked)}
            />
            Also list zero words and off bits (a longer scan)
          </label>
        </Disclosure>
        {started && (
          <p role="status" className="flex items-center gap-2 text-xs text-on-surface-muted">
            {running && <Loader2 className="size-3.5 animate-spin text-brand" />}
            {running
              ? `Reading ${blockProgress(p.driverId, cursor)}…`
              : done
                ? "Scan finished."
                : capped
                  ? `Stopped at ${MAX_BLOCK_ENTRIES} values; tick what you need, or narrow down by hand below.`
                  : `Stopped at ${blockProgress(p.driverId, cursor)}.`}{" "}
            {count} value{count === 1 ? "" : "s"} found.
          </p>
        )}
        {error && (
          <Notice tone="error">
            <DeviceErrorText message={error} />
          </Notice>
        )}
        {groups.map((g) => {
          const points = g.entries
            .map((e) => browseEntryToPoint(p.driverId, e, p.decodable))
            .filter((x): x is PickedPoint => x !== null);
          const openG = openGroups.has(g.key) || groups.length === 1;
          const on = points.filter((x) => p.pickedKeys.has(x.key)).length;
          return (
            <section key={g.key} className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => toggleGroup(g.key)}
                aria-expanded={openG}
                className="flex items-center gap-1.5 text-left text-sm font-medium text-on-surface"
              >
                {openG ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                {g.label}
                <span className="text-xs font-normal text-on-surface-muted">
                  {points.length} value{points.length === 1 ? "" : "s"}
                  {on > 0 ? `, ${on} ticked` : ""}
                </span>
              </button>
              {openG && (
                <PointTable
                  points={points}
                  view={p}
                  allOn={points.length > 0 && on === points.length}
                  empty="No values."
                  pairs={wordPairs(p.driverId, points)}
                />
              )}
            </section>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ── the point map (the same table and toolbar as the Modbus register map) ──

function PointMap({
  driver,
  driverId,
  deviceLabel,
  deviceProduct,
  picked,
  onPicked,
  onContinue,
  listsNothing,
  onAdd,
  existing,
}: PointPickerProps & {
  /** The device lists nothing, so Add row starts open. */
  listsNothing: boolean;
  onAdd: (p: PickedPoint) => void;
  existing: Set<string>;
}) {
  const toast = useToast();
  const [adding, setAdding] = useState<boolean | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [preview, setPreview] = useState<MapPreview | null>(null);
  const addOpen = !!driver && (adding ?? listsNothing);
  const types = driver?.data_types?.length ? driver.data_types : POINT_DATA_TYPES;
  const hasWordOrder = addressFieldsOf(driverId).includes("word_order");
  const problems = pointNameProblems(picked);
  const listed = new Set(picked.map((p) => pointIdentity(driverId, p.address)));
  const edit = (key: string, e: PointEdit) => onPicked(editPoint(picked, key, e));
  const at = (p: PickedPoint) => pointAddressLabel(p.address);

  const wordOrderColumn: ColumnDef<PickedPoint> = {
    id: "word_order",
    header: "Word order",
    unsortable: true,
    cell: (p) =>
      wordOrderApplies(driverId, p.data_type) ? (
        <WordOrderPicker
          label={`Word order of ${at(p)}`}
          value={pointWordOrder(driverId, p.address)}
          onChange={(w) => edit(p.key, { word_order: w })}
        />
      ) : null,
  };

  const columns: ColumnDef<PickedPoint>[] = [
    {
      id: "address",
      header: "Address",
      accessor: (p) => at(p),
      cell: (p) => <span className="font-mono text-on-surface">{at(p)}</span>,
    },
    {
      id: "type",
      header: "Type",
      accessor: (p) => p.data_type,
      cell: (p) => (
        <NativeSelect
          aria-label={`Type of ${at(p)}`}
          value={p.data_type}
          onChange={(e) => edit(p.key, { type: e.target.value as PointDataType })}
          className="h-7 w-24 px-1 font-mono text-xs"
        >
          {(types.includes(p.data_type) ? types : [p.data_type, ...types]).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </NativeSelect>
      ),
    },
    ...(hasWordOrder ? [wordOrderColumn] : []),
    {
      id: "name",
      header: "Name",
      accessor: (p) => p.metric,
      cell: (p) => {
        const problem = problems.get(p.key);
        return (
          <span className="flex flex-col gap-0.5">
            <Input
              aria-label={`Name of ${at(p)}`}
              value={p.metric}
              onChange={(e) => edit(p.key, { name: e.target.value })}
              aria-invalid={!!problem}
              className="h-7 w-40 font-mono text-xs"
            />
            {problem && <span className="text-[11px] text-danger">{problem}</span>}
          </span>
        );
      },
    },
    {
      id: "unit",
      header: "Unit",
      accessor: (p) => p.unit ?? "",
      cell: (p) => (
        <Input
          aria-label={`Unit of ${at(p)}`}
          value={p.unit ?? ""}
          onChange={(e) => edit(p.key, { unit: e.target.value })}
          placeholder="°C"
          className="h-7 w-16 text-xs"
        />
      ),
    },
    {
      id: "scale",
      header: "Scale",
      unsortable: true,
      cell: (p) => (
        <ScaleInput label={`Scale of ${at(p)}`} value={p.scale} onCommit={(scale) => edit(p.key, { scale })} />
      ),
    },
    {
      id: "value",
      header: "Sampled",
      unsortable: true,
      align: "right",
      cell: (p) => {
        const v = pointSampled(p);
        return (
          <span className="font-mono text-on-surface" title={v.raw ? `read ${v.raw} x scale ${p.scale}` : undefined}>
            {v.text}
            {p.unit && v.text !== "—" ? <span className="text-on-surface-muted"> {p.unit}</span> : null}
          </span>
        );
      },
    },
    {
      id: "remove",
      header: "",
      unsortable: true,
      cell: (p) => (
        <button
          type="button"
          aria-label={`Remove ${p.metric || at(p)}`}
          title="Remove this point"
          onClick={() => onPicked(picked.filter((x) => x.key !== p.key))}
          className="rounded p-1 text-on-surface-muted hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Trash2 className="size-3.5" />
        </button>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ListChecks className="size-5 text-brand" />
          Point map — {deviceLabel}
        </CardTitle>
        <CardDescription>Set each point&apos;s type, name, unit and scale; they go live as set here.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            aria-expanded={addOpen}
            disabled={!driver}
            title={driver ? undefined : "Adding a point needs this gateway's driver list."}
            onClick={() => setAdding(!addOpen)}
          >
            <Plus /> Add row
          </Button>
          <MapFileButtons
            driverId={driverId}
            dataTypes={driver?.data_types}
            records={picked.map(pointToRecord)}
            deviceLabel={deviceLabel}
            helpOpen={helpOpen}
            onHelp={setHelpOpen}
            onPreview={setPreview}
            onError={(msg) => toast.push("error", msg)}
          />
          <KnownMapPicker
            driverId={driverId}
            dataTypes={driver?.data_types}
            deviceProduct={deviceProduct}
            onPreview={setPreview}
            onError={(msg) => toast.push("error", msg)}
          />
        </div>
        {addOpen && driver && (
          <ManualPointForm driver={driver} onAdd={onAdd} existing={existing} onClose={() => setAdding(false)} />
        )}
        {helpOpen && <MapFormatHelp driverId={driverId} dataTypes={driver?.data_types} />}
        {preview && (
          <ImportPreview
            preview={preview}
            driverId={driverId}
            listed={listed}
            onCancel={() => setPreview(null)}
            onApply={(records, mode) => {
              const res = mergePoints(driverId, picked, records, mode);
              onPicked(res.points);
              setPreview(null);
              toast.push(
                "ok",
                mode === "replace"
                  ? `Loaded ${res.added} points from ${preview.file}.`
                  : `${preview.file}: ${res.added} added, ${res.updated} updated.`,
              );
            }}
          />
        )}
        <Table
          getRowId={(p) => p.key}
          columns={columns}
          data={picked}
          hideSearch={picked.length <= 8}
          hidePagination
          emptyTitle="No points picked yet."
          emptyDescription="Tick points above, add a row by hand, or import a map."
        />
        <MapFooter count={picked.length} onContinue={onContinue} />
      </CardContent>
    </Card>
  );
}

/** Add row: the driver's address fields (its address_schema), a name and a type. */
function ManualPointForm({
  driver,
  onAdd,
  existing,
  onClose,
}: {
  driver: DriverInfo;
  onAdd: (p: PickedPoint) => void;
  existing: Set<string>;
  onClose: () => void;
}) {
  const metricId = useId();
  const typeId = useId();
  const types = driver.data_types?.length ? driver.data_types : POINT_DATA_TYPES;
  const [values, setValues] = useState<FormValues>(() => formDefaults(driver.address_schema));
  const [metric, setMetric] = useState("");
  const [dataType, setDataType] = useState<PointDataType>(types.includes("int16") ? "int16" : types[0]);
  const [tried, setTried] = useState(false);

  const address = formToParams(driver.address_schema, values);
  const check = validateParams(driver.address_schema, address);
  // The rules a schema cannot say (an S7 DB needs db >= 1, MELSEC device codes, the OPC UA node id shape).
  const contract = check.valid ? validateAddress(driver.id, address) : null;
  const key = addressKey(address);
  const dup = existing.has(key);
  const metricOk = METRIC_PATTERN.test(metric.trim());

  const add = () => {
    setTried(true);
    if (!check.valid || contract || !metricOk || dup) return;
    onAdd({ key, metric: metric.trim(), address, data_type: dataType, label: pointAddressLabel(address) });
    setMetric("");
    setTried(false);
  };

  return (
    <section aria-label="Add a point" className="flex flex-col gap-3 rounded-lg border border-border bg-surface-elevated p-3">
      <SchemaForm
        schema={driver.address_schema}
        values={values}
        onChange={setValues}
        errors={tried ? check.errors : {}}
        compact
      />
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={metricId} className="text-[11px] font-medium text-on-surface">
            Metric name <span className="text-danger">*</span>
          </label>
          <Input
            id={metricId}
            className="h-8 w-48 font-mono text-xs"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            placeholder="bath_temp"
            aria-invalid={tried && !metricOk}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={typeId} className="text-[11px] font-medium text-on-surface">
            Data type
          </label>
          <NativeSelect
            id={typeId}
            className="h-8 w-32 text-xs"
            value={dataType}
            onChange={(e) => setDataType(e.target.value as PointDataType)}
          >
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </NativeSelect>
        </div>
        <Button size="sm" onClick={add}>
          <Plus /> Add point
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Done
        </Button>
      </div>
      {tried && !metricOk && <p className="text-[11px] text-danger">Name the point: letters, digits, _ . or - only.</p>}
      {tried && contract && <p className="text-[11px] text-danger">{contract}</p>}
      {dup && <p className="text-[11px] text-warning">That address is already picked.</p>}
    </section>
  );
}

// ── notice ──────────────────────────────────────────────────────────────────

function Notice({ tone, children }: { tone: "error" | "info"; children: React.ReactNode }) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-2 rounded-md border p-3 text-xs",
        tone === "error" ? "border-danger/40 bg-danger/5" : "border-warning/40 bg-warning/5",
      )}
    >
      <TriangleAlert className={cn("mt-0.5 size-3.5 shrink-0", tone === "error" ? "text-danger" : "text-warning")} />
      <span className="text-on-surface-muted">{children}</span>
    </div>
  );
}
